import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import { ActionResult } from '~/models/shared/result'
import { formatPhoto } from '~/services/photos/formatters'
import {
  ClassifiedPhotoUploadResult,
  ClassifiedsPhotosResults
} from '~/models/photos/classified'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { noop } from '~/utils/function'
import { AxiosProgressEvent } from '~/models/http'

@containerScoped()
export class PhotosService {
  private url: string = ''

  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  public setUrl(url: string) {
    this.url = url
    return this
  }

  getPhotosInfo(): Promise<ClassifiedsPhotosResults> {
    return this.requestBuilder
      .request('get', this.url)
      .map(body => this.formatPhotosInfo(body.data))
      .send()
  }

  updatePhotos(
    photosNames: string[],
    panoramic: boolean = false
  ): Promise<ActionResult> {
    return this.requestBuilder
      .request('patch', this.url)
      .data({ photos: photosNames, panoramic })
      .map(body => ({ message: body.message, status: body.status }))
      .send()
  }

  deletePhoto(
    photoName: string,
    panoramic: boolean = false
  ): Promise<ActionResult> {
    return this.requestBuilder
      .request('delete', this.url)
      .data({ name: photoName, panoramic })
      .map(body => ({ message: body.message, status: body.status }))
      .validate(body => body.message)
      .send()
  }

  uploadPhoto(
    file: File,
    useCar1FileStorage: boolean = false,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void = noop,
    panoramic: boolean = false
  ): Promise<ClassifiedPhotoUploadResult> {
    const formData = new FormData()
    formData.append('file', file)

    return this.requestBuilder
      .request('post', this.url)
      .data(formData)
      .params({
        panoramic,
        'car-file-storage': useCar1FileStorage ? 1 : undefined
      })
      .onUploadProgress(progressEvent => onUploadProgress(progressEvent))
      .map(body => this.formatUploadResult(body.data))
      .send()
  }

  private formatUploadResult(d: any): ClassifiedPhotoUploadResult {
    return {
      attached: d.attached,
      file: formatPhoto(d.file)
    }
  }

  private formatPhotosInfo(d: any): ClassifiedsPhotosResults {
    const { limits, photos } = d
    return {
      limits,
      photos: {
        native: photos?.native?.values.map(formatPhoto) || [],
        panoramic: photos?.panoramic?.values.map(formatPhoto) || []
      }
    }
  }
}
